import '@/global.css'
import { Input } from "@/components/ui/input"
import { Link } from "react-router";
import { ROUTE } from '@/routes/constants'
import { useCallback, useState } from 'react'
import { Role, SCHOOLS } from '@/utils/constants'
import { registerStudent, registerTeacher, requestVerification, verifyRecaptcha } from '@/services/generatedAPIs'
import { generateSalt, generateVerificationToken, hashPassword } from '@/lib/crypto'
import { toast } from 'sonner'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { isEN } from '@/utils/i18n'
import ReCAPTCHA from "react-google-recaptcha";
import { useAsyncAction } from '@/hooks/use-async-action'
import { useTranslation } from 'react-i18next';

// 移动端注册表单：视觉沿用设计稿配色/圆角（绿色身份胶囊、蓝色创建按钮），布局改为流式自适应。
function MobileSignupForm() {
  const { t, i18n } = useTranslation();
  const [signupMessage, setSignupMessage] = useState<string>("");
  const [role, setRole] = useState<string>(Role.STUDENT);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [sex, setSex] = useState("X");
  const [email, setEmail] = useState<string>("");
  const [showRecaptcha, setShowRecaptcha] = useState<boolean>(false);
  const [schoolName, setSchoolName] = useState<string>("");

  function onSignup(formData: FormData) {
    if (formData.get("password") !== formData.get("confirm-password")) {
      setSignupMessage(t("user.confirm-password-mismatch"));
      return;
    }
    function onOK() { alert(t("user.sign-up-success")); setSignupMessage(t("user.sign-up-success")); }
    function onFail() { setSignupMessage(t("user.sign-up-failed")); }
    execute(async () => {
      const salt = generateSalt();
      if (role === Role.TEACHER) {
        const res = await registerTeacher({
          email: formData.get("email") as string, name: formData.get("name") as string,
          nameEn: formData.get("enname") as string,
          password: hashPassword(formData.get("password") as string + salt),
          salt, schoolName, sex, captcha: formData.get("captcha") as string,
        });
        if (res.code === 200) onOK(); else onFail();
      } else {
        const res = await registerStudent({
          email: formData.get("email") as string, name: formData.get("name") as string,
          nameEn: formData.get("enname") as string,
          password: hashPassword(formData.get("password") as string + salt),
          salt, schoolName,
          classNumber: formData.has("classID") ? Number(formData.get("classID") as string) : undefined,
          schoolNumber: formData.has("schoolID") ? Number(formData.get("schoolID") as string) : undefined,
          gradeLevel: Number(formData.get("grade") as string),
          strn: '', sex, captcha: formData.get("captcha") as string,
        });
        if (res.code === 200) onOK(); else onFail();
      }
    });
  }

  const onVerifyRecaptcha = useCallback(async (value: string | null) => {
    if (!value) return;
    try {
      const rsp = await verifyRecaptcha({ "g-recaptcha-response": value });
      if (rsp.code === 200 && rsp.data && email) {
        const rsp2 = await requestVerification({ t: generateVerificationToken(email) });
        if (rsp2.code === 200) { toast(t('component.sendsuccess')); return; }
        toast(rsp2.message);
        return;
      }
      toast(t('common.apierror'));
    } catch {
      toast(t('common.apierror'));
    } finally {
      setShowRecaptcha(false);
    }
  }, [email, t]);

  const isStudent = role === Role.STUDENT;
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: '#575C60', marginBottom: 6, display: 'block' };
  const inputStyle: React.CSSProperties = { width: '100%', height: 40, borderRadius: 10, background: '#FFFFFF', border: '1px solid #E5E7EB', padding: '0 14px', fontSize: 14, color: '#1F1F1F' };
  const pillStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, height: 44, borderRadius: 22, border: 0, cursor: 'pointer',
    background: active ? '#9CEA4F' : '#F3F3F4', color: active ? '#000' : '#575C60',
    fontSize: 15, fontWeight: 500,
  });

  return (
    <>
      {showRecaptcha && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <ReCAPTCHA sitekey={import.meta.env.VITE_RECAPTCHA_SITEKEY} onChange={onVerifyRecaptcha} />
        </div>
      )}
      <form action={onSignup} style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12 }}>
        <h2 style={{ margin: 0, textAlign: 'center', fontSize: 24, fontWeight: 600, color: '#2A2F32' }}>{t('user.signup-title')}</h2>

        <div style={{ fontSize: 13, color: '#575C60', marginTop: 4 }}>{t('user.role-desc')}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={() => setRole(Role.STUDENT)} style={pillStyle(isStudent)}>{t('user.student')}</button>
          <button type="button" onClick={() => setRole(Role.TEACHER)} style={pillStyle(!isStudent)}>{t('user.teacher')}</button>
        </div>

        {signupMessage && <p style={{ margin: 0, fontSize: 13, color: '#EF4444' }}>{signupMessage}</p>}

        <div style={{ fontSize: 13, color: '#575C60', marginTop: 4 }}>{t('user.info-desc')}</div>

        <div>
          <label htmlFor="name" style={labelStyle}>{t('user.name')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Input id="name" name="name" type="text" required style={inputStyle} />
        </div>

        <div>
          <label htmlFor="enname" style={labelStyle}>{t('user.enname')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Input id="enname" name="enname" type="text" required style={inputStyle} />
        </div>

        <div>
          <label htmlFor="sex" style={labelStyle}>{t('user.sex')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Select name="sex" value={sex} onValueChange={setSex} required>
            <SelectTrigger style={inputStyle}><SelectValue id="sex" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="X">{t('common.nottosay')}</SelectItem>
              <SelectItem value="male">{t('common.male')}</SelectItem>
              <SelectItem value="female">{t('common.female')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="schoolname" style={labelStyle}>{t('user.schoolname')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Select name="schoolname" onValueChange={setSchoolName} value={schoolName} required>
            <SelectTrigger style={inputStyle}><SelectValue id="schoolname" /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {SCHOOLS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{isEN(i18n) ? s.en : s.zhHK}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {isStudent && (
          <>
            <div>
              <label htmlFor="grade" style={labelStyle}>{t('common.grade')}<span style={{ color: '#E5484D' }}>*</span></label>
              <Select name="grade" required>
                <SelectTrigger style={inputStyle}><SelectValue id="grade" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="7">{t('teacher.s1')}</SelectItem>
                    <SelectItem value="8">{t('teacher.s2')}</SelectItem>
                    <SelectItem value="9">{t('teacher.s3')}</SelectItem>
                    <SelectItem value="10">{t('teacher.s4')}</SelectItem>
                    <SelectItem value="11">{t('teacher.s5')}</SelectItem>
                    <SelectItem value="12">{t('teacher.s6')}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="classID" style={labelStyle}>{t('common.classID')}</label>
              <Input id="classID" name="classID" type="number" style={inputStyle} />
            </div>

            <div>
              <label htmlFor="schoolID" style={labelStyle}>{t('common.schoolID')}</label>
              <Input id="schoolID" name="schoolID" type="number" style={inputStyle} />
            </div>
          </>
        )}

        <div>
          <label htmlFor="email" style={labelStyle}>{t('user.email')}<span style={{ color: '#E5484D' }}>*</span></label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              id="email" name="email" type="email" required placeholder="m@example.com"
              onInvalid={(e: any) => { e.target.setCustomValidity(t('user.email-error')); }}
              onInput={(e: any) => { e.target.setCustomValidity(""); setEmail(e.target.value); }}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowRecaptcha(true); }}
              style={{ width: 110, height: 40, borderRadius: 10, background: '#9CEA4F', color: '#000', border: 0, fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('component.getcaptcha')}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="captcha" style={labelStyle}>{t('user.captcha')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Input id="captcha" name="captcha" type="text" required style={inputStyle} />
        </div>

        <div>
          <label htmlFor="password" style={labelStyle}>{t('user.password')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Input
            id="password" name="password" type="password" required
            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(""); }}
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="confirm-password" style={labelStyle}>{t('user.confirm-password')}<span style={{ color: '#E5484D' }}>*</span></label>
          <Input
            id="confirm-password" name="confirm-password" type="password" required
            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(""); }}
            style={inputStyle}
          />
        </div>

        <div style={{ fontSize: 12, color: '#575C60', textAlign: 'center', lineHeight: '18px', marginTop: 4 }}>
          {t('user.create-account-msg')}
          <Link to={ROUTE.PRIVACY_POLICY} style={{ marginLeft: 4, color: '#5BC9E8' }} className="hover:underline">{t('user.privacypolicy')}</Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            marginTop: 6, width: '100%', height: 52, borderRadius: 26,
            background: '#5BC9E8', color: '#FFFFFF', border: 0,
            fontSize: 18, fontWeight: 500, cursor: 'pointer', opacity: isLoading ? 0.6 : 1,
          }}
          className="hover:brightness-[0.97]"
        >
          {t('user.create-account')}
        </button>

        <div style={{ marginTop: 8, textAlign: 'center', fontSize: 14, color: '#575C60' }}>
          {t('user.signup-comment')}
          <Link to={ROUTE.LOGIN} style={{ marginLeft: 4, color: '#5BC9E8' }} className="underline underline-offset-4">{t('user.sign-in')}</Link>
        </div>
      </form>
    </>
  );
}

export default MobileSignupForm;
